import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useChangelogSeen, useMarkChangelogSeen, useVisibleReleases } from "@/hooks/use-changelog";
import { shouldShowChangelogPopup } from "@/lib/changelog/registry";

/**
 * «Nytt i Revehiet»-popup (VIB-2621). Vises én gang per bruker når nyeste
 * synlige UTRULLING er en annen enn `users.changelog_seen_id`. Systemrapporten
 * (én oppføring per PR) gir aldri popup (VIB-2832).
 * Både «Les mer» og «Lukk» kvitterer, så den ikke kommer igjen.
 *
 * Hopper over ved impersonering: sett-status er en egenskap ved den faktisk
 * innloggede brukeren, og en superadmin som tester som noen andre skal verken
 * få popupen eller skrive kvitteringen på egen rad i den andres navn.
 */
export default function ChangelogDialog() {
  const { selfUser, isImpersonating } = useImpersonation();
  const releases = useVisibleReleases();
  const latest = releases[0];
  const { data: seenId } = useChangelogSeen(!isImpersonating ? selfUser?.id : null);
  const markSeen = useMarkChangelogSeen();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const shouldShow =
    !isImpersonating && !!selfUser && shouldShowChangelogPopup(latest, seenId);

  useEffect(() => {
    if (shouldShow) setOpen(true);
  }, [shouldShow]);

  if (!latest || !selfUser) return null;

  const acknowledge = () => {
    setOpen(false);
    if (seenId !== latest.id) markSeen.mutate({ userId: selfUser.id, entryId: latest.id });
  };

  const readMore = () => {
    acknowledge();
    navigate("/hjelp/nytt");
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : acknowledge())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nytt i Revehiet</DialogTitle>
          <DialogDescription className="text-left">
            <span className="block text-xs font-semibold uppercase tracking-wide text-primary">
              {latest.module}
            </span>
            <span className="mt-1 block font-medium text-foreground">{latest.title}</span>
            <span className="mt-1 block">{latest.intro}</span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
          <Button variant="outline" onClick={acknowledge}>
            Lukk
          </Button>
          <Button onClick={readMore}>Les mer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
